import * as React from 'react'
import { createContext, useContext } from 'react'

interface Language {
  default?: boolean
  path: string
  locale: string
  siteLanguage: string
  ogLang: string
  [key: string]: string | boolean
}

interface Languages {
  [key: string]: Language
}

export interface I18nProviderProps {
  basePath: string
  locale: string
  languages: Languages
  translations: any
}

export interface I18nContextProps {
  basePath
  locale: string
  languages: Languages
  basePathI18n: string
  getLangPath: (slug?: string) => string
  getLangPathForLocale: (locale: string, slug?: string) => string
  isCompatible(locale: string): boolean
  t(desc: string): string
}

const I18nContext = createContext<I18nContextProps>(null!)

export const I18nProvider: React.FC<I18nProviderProps> = ({
  basePath,
  locale,
  languages,
  translations,
  children
}) => {
  const language = languages[locale]
  const getLangPath = (slug = '') => {
    return `${basePath}${
      language.default ? '' : `${language.path}/`
    }${slug.replace(/^\//, '')}`
  }

  const context = {
    basePath,
    basePathI18n: getLangPath(),
    locale,
    languages,
    getLangPath,
    getLangPathForLocale: (locale, slug = '') => {
      const otherLanguage = languages[locale]
      return `${basePath}${
        otherLanguage.default ? '' : `${otherLanguage.path}/`
      }${slug.replace(/^\//, '')}`
    },
    isCompatible: (testLocale: string) =>
      testLocale === 'global' || locale.startsWith(testLocale),
    t: (desc: string) => getDescendantProp(translations, desc) || desc
  }

  return <I18nContext.Provider value={context}>{children}</I18nContext.Provider>
}

export const useI18n = () => useContext(I18nContext)

export const withI18n = (Component) => {
  return function i18WrapperComponent(props) {
    return (
      <I18nContext.Consumer>
        {(state) => <Component {...props} i18n={state} />}
      </I18nContext.Consumer>
    )
  }
}

export const getLangPath = (language: Language, basePath, slug = '') => {
  return `${basePath}${language.default ? '' : `${language.path}/`}${slug}`
}

function getDescendantProp(obj, desc) {
  const arr = desc.split('.')
  // eslint-disable-next-line no-cond-assign
  while (arr.length && (obj = obj[arr.shift()])) {
    /** no-pp */
  }
  return obj
}
